import { useCallback } from 'react';
import { useQuery, useQueryClient, QueryFunctionContext } from 'react-query';
import { getCompanyFeed } from '@simplywallst/services';
import { getCompanyFeedItems } from '@components/Dashboard/hooks/useFeed/selector';
import { SEEKING_ALPHA } from '@/constants/featureFlags';
import { useFlags } from '@/hooks/useFlags';

export async function queryFn(
  context: QueryFunctionContext<[string, string] | [string, string, number]>
) {
  const { 1: companyId, 2: limit } = context.queryKey;
  const response = await getCompanyFeed({ lookup: companyId, limit });
  return response.data.data;
}

export function getQueryKey(
  lookup: string,
  limit = 0
): [string, string] | [string, string, number] {
  if (limit) {
    return ['COMPANY_FEED', lookup, limit];
  }
  return ['COMPANY_FEED', lookup];
}
interface Options {
  limit?: number;
  /** temporary: once the a/b test is finished */
  showAllEvents?: boolean;
}
export default function useCompanyFeed(
  companyId: string | null,
  options?: Options
) {
  const flag = useFlags(SEEKING_ALPHA.KEY);
  const queryClient = useQueryClient();
  const { isLoading, data } = useQuery(
    getQueryKey(companyId as string, options?.limit),
    queryFn,
    {
      initialData: () => {
        /** Reuse cache from watchlist/dashboard feed if available **/
        const existingData = queryClient.getQueryData<
          Awaited<ReturnType<typeof queryFn>>
        >(getQueryKey(companyId as string));
        return existingData;
      },
      staleTime: 3600000,
      retry: 1,
      enabled:
        typeof companyId === 'string' &&
        companyId !== '' &&
        (options?.showAllEvents === true || flag.status === 'success'),
      refetchOnMount: false,
      select: useCallback(
        (companyEvents: Awaited<ReturnType<typeof queryFn>>) => {
          try {
            let selectedEvents = getCompanyFeedItems(
              companyEvents.events.data,
              {
                canonicalUrl: companyEvents.canonical_url,
                hideSeekingAlphaArticles:
                  options?.showAllEvents === true
                    ? false
                    : typeof flag.variant === 'undefined' ||
                      flag.variant ===
                        SEEKING_ALPHA.VARIANTS.NO_SEEKING_ALPHA_ARTICLES,
              }
            );
            if (typeof options?.limit === 'number') {
              selectedEvents = selectedEvents.slice(0, options.limit);
            }
            return {
              name: companyEvents.name,
              companyId: companyEvents.id,
              tickerSymbol: companyEvents.ticker_symbol,
              canonicalUrl: companyEvents.canonical_url,
              events: selectedEvents,
            };
          } catch (e) {
            return undefined;
          }
        },
        [companyId, options?.limit, options?.showAllEvents, flag.variant]
      ),
    }
  );

  return {
    isLoading:
      isLoading ||
      (options?.showAllEvents === false &&
        (flag.status === 'loading' || flag.status === 'idle')),
    data,
  };
}
